import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { api } from '../lib/api';

interface User {
  id: number;
  email: string;
  contactPerson: string;
  phone: string;
  isAdmin?: boolean;
  isVerified?: boolean;
  jobTitle?: string;
  advisorNumber?: string;
  companyName?: string;
  companyLogoUrl?: string;
  companyAddress?: string;
  plan?: 'free' | 'pro' | 'team';
  planExpiresAt?: string | null;
  teamId?: number | null;
  teamRole?: 'owner' | 'admin' | 'member' | null;
  teamName?: string | null;
  filenamePattern?: string | null;
  notifySignedUploadEmail?: boolean;
  notifyPayoutDigest?: boolean;
}

interface AuthResponse {
  access_token: string;
  user: User;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  register: (data: {
    email: string;
    password: string;
    contactPerson: string;
    companyName: string;
    phone: string;
  }) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  acceptInvite: (token: string, data: { password: string; vorname?: string; nachname?: string }) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  resendVerification: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    api
      .get<User>('/api/auth/me')
      .then(setUser)
      .catch(() => {
        localStorage.removeItem('auth_token');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      contactPerson: string;
      companyName: string;
      phone: string;
    }) => {
      const res = await api.post<AuthResponse>('/api/auth/register', data);
      localStorage.setItem('auth_token', res.access_token);
      setUser(res.user);
    },
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<AuthResponse>('/api/auth/login', {
      email,
      password,
    });
    localStorage.setItem('auth_token', res.access_token);
    sessionStorage.removeItem('expiry_modal_shown');
    setUser(res.user);
  }, []);

  // Team-Einladung annehmen + Konto in einem Schritt (Passwort, optional Name) → direkt eingeloggt.
  const acceptInvite = useCallback(async (token: string, data: { password: string; vorname?: string; nachname?: string }) => {
    const res = await api.post<AuthResponse>(`/api/auth/accept-register/${token}`, data);
    localStorage.setItem('auth_token', res.access_token);
    sessionStorage.removeItem('expiry_modal_shown');
    setUser(res.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('expiry_modal_shown');
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data: Partial<User>) => {
    const updated = await api.patch<User>('/api/auth/profile', data);
    setUser(updated);
  }, []);

  const resendVerification = useCallback(async () => {
    await api.post('/api/auth/resend-verification', {});
  }, []);

  const refreshUser = useCallback(async () => {
    const updated = await api.get<User>('/api/auth/me');
    setUser(updated);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        register,
        login,
        acceptInvite,
        logout,
        updateProfile,
        resendVerification,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth muss innerhalb eines AuthProviders verwendet werden');
  }
  return context;
}
